import { useState } from 'react';
import clsx from 'clsx';
import { Spinner } from '../spinner';
import LoadMoreIcon from '@images/load-more.svg';
import { HelpersUtils } from '@shared/utils';
import { MobileFullView, MobileRow } from './widgets';
import SortableIcon from '@assets/icons/sortable.svg';
import { ColumnsType } from '@pages/payments/widgets';

interface TableType {
  className?: string;
  columns: ColumnsType[];
  items: { value: any }[][];
  loading: boolean;
  isScrollable?: boolean;
  sortOrder?: string;
  accountTab?: boolean;
  onClick?: (rowIndex: number) => void;
}

interface CurrentRowType {
  value: string;
}

export const Table = (props: TableType) => {
  const [visibleCount, setVisibleCount] = useState(10);
  const [mobileVisibleCount, setMobileVisibleCount] = useState(10);
  const [currentRow, setCurrentRow] = useState<CurrentRowType[] | null>(null);

  const desktopItemsToShow = props.items.slice(0, visibleCount);
  const mobileItemsToShow = props.items.slice(0, mobileVisibleCount);

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + 10);
    setMobileVisibleCount((prev) => prev + 10);
  };

  const hideFullView = () => {
    setCurrentRow(null);
  };

  const showFullView = (row: CurrentRowType[]) => () => {
    setCurrentRow(row);
  };

  return (
    <div className={clsx('mt-6 flex w-full flex-col', props.className)}>
      {HelpersUtils.isMobile.any() ? (
        <div className='mx-2.5'>
          <div className='relative z-10 flex h-[65px] w-full items-center justify-center rounded bg-[#1f1f1f] shadow-[3px_3px_6px_0_rgba(0,0,0,0.23),inset_0_2px_0_0_rgba(52,52,52,0.5)]'>
            <div className='text-center text-sm font-semibold text-[#929292] uppercase'>Transactions</div>
          </div>
          <div className='table-mobile-content'>
            {props.loading ? (
              <Spinner className='m-auto py-2' />
            ) : mobileItemsToShow.length > 0 ? (
              mobileItemsToShow.map((row, i) => <MobileRow key={i} data={row} handleClick={showFullView(row)} accountTab={props.accountTab} />)
            ) : (
              <div className='py-4 text-center text-sm text-[#929292]'>No data available</div>
            )}
          </div>
          {props.items.length > mobileVisibleCount && (
            <div className='flex justify-center py-4'>
              <div className='flex cursor-pointer items-center select-none' onClick={handleLoadMore}>
                <LoadMoreIcon className='h-6 w-6' />
                <div className='ml-3 text-xs text-[#929292] uppercase'>Show More</div>
              </div>
            </div>
          )}

          {currentRow && <MobileFullView data={currentRow} columns={props.columns} handleClose={hideFullView} />}
        </div>
      ) : (
        <>
          <div className={clsx('flex w-full bg-white', props.isScrollable && 'overflow-x-auto')}>
            <table className='w-full min-w-[610px] text-sm'>
              <thead className='rounded-tl rounded-tr bg-[#1f1f1f] text-xs font-semibold uppercase shadow-[inset_0_2px_0_0_rgba(52,52,52,0.5)]'>
                <tr>
                  {props.columns.map((column, key) => (
                    <th
                      key={key}
                      scope='col'
                      className={clsx(
                        'relative overflow-hidden border-l border-l-[#36322b] px-2 text-sm text-[#929292] uppercase',
                        key === 0 && 'border-l-0',
                      )}
                    >
                      <div
                        className='flex cursor-pointer items-center justify-center gap-2 !py-6'
                        onClick={column.onSort ? column.onSort : undefined}
                      >
                        {column.title}
                        {column.onSort && (
                          <SortableIcon
                            className={clsx('transition-transform', {
                              'rotate-180': props.sortOrder === 'asc',
                            })}
                          />
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className='bg-[#efefef]'>
                {props.loading ? (
                  <tr>
                    <td colSpan={props.columns.length} className='overflow-hidden py-4'>
                      <Spinner />
                    </td>
                  </tr>
                ) : desktopItemsToShow.length > 0 ? (
                  desktopItemsToShow.map((rowItem, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className={clsx(
                        'px-7',
                        props.onClick && 'cursor-pointer',
                        rowIndex !== desktopItemsToShow.length - 1 && 'border-b border-b-[#d5d5d5]',
                        rowIndex % 2 === 0 && 'bg-[#f5f5f5]',
                      )}
                      onClick={() => props.onClick?.(rowIndex)}
                    >
                      {rowItem.map((cell, cellIndex) => (
                        <th
                          key={cellIndex}
                          scope='row'
                          className={clsx(
                            'overflow-hidden border-l border-l-[#d5d5d5] py-4 text-center text-sm font-medium text-[#343434] uppercase',
                            cellIndex === 0 && 'border-l-0',
                            cellIndex !== 0 && 'px-2',
                          )}
                        >
                          <div className='flex justify-center text-nowrap'>{cell.value}</div>
                        </th>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={props.columns.length} className='px-5 py-8 text-center text-base text-[#444] uppercase'>
                      Transaction list is empty
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {props.items.length > visibleCount && (
            <div className='flex justify-center py-9'>
              <div className='flex cursor-pointer items-center select-none' onClick={handleLoadMore}>
                <LoadMoreIcon className='h-8 w-8' />
                <div className='ml-4 text-xs text-[#929292] uppercase'>Show More</div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
